import React from 'react';

interface ButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'small' | 'medium' | 'large';
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'large',
  onClick,
  className = '',
  disabled = false,
  icon,
  iconPosition = 'left'
}) => {
  
  // Size-based styling
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return "px-6 py-3 text-[20px] leading-[26px] tracking-[-0.4px]";
      case 'medium':
        return "px-8 py-4 text-[20px] leading-[30px] tracking-[-0.48px]";
      case 'large':
      default:
        return "px-16 py-8 text-[32px] leading-[40px] tracking-[-0.64px]";
    }
  };

  const baseClasses = `flex justify-center items-center gap-2.5 flex-1 self-stretch rounded-brand-button transition-all duration-200 leading-none ${getSizeClasses()}`;
  
  const primaryClasses = "bg-brand-primary active:bg-brand-text-muted";
  
  const secondaryClasses = "bg-transparent border-2 border-brand-text-primary active:bg-brand-border";
  
  const tertiaryClasses = "bg-brand-background border-2 border-brand-border";
  
  const disabledClasses = "bg-brand-surface border-2 border-brand-border cursor-not-allowed";
  
  const variantClasses = disabled ? disabledClasses : (variant === 'primary' ? primaryClasses : variant === 'secondary' ? secondaryClasses : tertiaryClasses);
  
  const finalClasses = `${baseClasses} ${variantClasses} ${disabled ? '' : 'cursor-pointer'} ${className}`;
  
  return (
    <button
      className={finalClasses}
      onClick={onClick}
      disabled={disabled}
      data-variant={variant}
    >
      <div 
        className={`font-brand-normal text-center text-nowrap flex items-center gap-2 ${
          disabled
            ? 'text-brand-text-muted font-brand-light'
            : variant === 'primary' 
            ? 'text-brand-background font-brand-medium' 
            : variant === 'secondary'
            ? 'text-brand-text-primary font-brand-light'
            : 'text-brand-text-primary font-brand-light'
        }`}
        style={{ 
          fontFamily: 'var(--brand-font-button)',
          textTransform: 'var(--brand-button-text-transform)' as React.CSSProperties['textTransform'],
          letterSpacing: 'var(--brand-button-letter-spacing)',
          color: disabled ? 'var(--brand-text-muted)' : (variant === 'secondary' ? 'var(--brand-text-primary)' : variant === 'tertiary' ? 'var(--brand-text-primary)' : 'var(--brand-button-color)')
        }}
      >
        {icon && iconPosition === 'left' && icon}
        <p className="block whitespace-pre">{children}</p>
        {icon && iconPosition === 'right' && icon}
      </div>
    </button>
  );
};

export default Button;
